<template>
  <div class="privacy-policy-page">
    <!-- Brand Header -->
    <header class="brand-header">
      <div class="header-container">
        <div class="brand-info">
          <img src="/img/logo.png" alt="SaveAnimals Logo" class="brand-logo" />
          <div class="brand-text">
            <h1 class="brand-title">SaveAnimals</h1>
            <p class="brand-tagline">Wildlife Rescue Tracking System</p>
          </div>
        </div>
        <div class="partner-logos">
          <img src="/img/wti_logo.png" alt="Wildlife Trust of India" class="partner-logo" />
          <img src="/img/leopardlogo.png" alt="Leopard Tech Labs" class="partner-logo" />
        </div>
      </div>
    </header>

    <!-- Main Content Area -->
    <main class="policy-main-content">
      <div class="policy-container">
        <!-- Sidebar Navigation (Desktop) -->
        <aside class="policy-sidebar">
          <div class="sidebar-sticky">
            <h4 class="sidebar-title">Sections</h4>
            <ul class="nav-list">
              <li v-for="section in sections" :key="section.id" :class="{ active: activeSection === section.id }">
                <a :href="'#' + section.id" @click.prevent="scrollTo(section.id)">
                  {{ section.title }}
                </a>
              </li>
            </ul>
          </div>
        </aside>

        <!-- Document Body -->
        <article class="policy-article">
          <div class="intro-card card-glass">
            <h2 class="doc-title">Privacy Policy</h2>
            <div class="meta-row">
              <span class="meta-item"><strong class="green-label">Last Updated:</strong> July 16, 2026</span>
              <span class="meta-item"><strong class="green-label">Version:</strong> 1.2.0</span>
            </div>
            <p class="intro-text">
              Welcome to <strong>SaveAnimals</strong> (accessible via SaveAnimals.in and our mobile application). We are committed to protecting your personal information and your privacy. This Privacy Policy explains how <strong>Leopard Tech Labs Pvt. Ltd.</strong> ("we", "us", or "our"), in partnership with the <strong>Wildlife Trust of India (WTI)</strong> and the <strong>International Fund for Animal Welfare (IFAW)</strong>, collects, uses, discloses, and safeguards your information when you use our mobile application and admin portal.
            </p>
          </div>

          <!-- Policy Sections -->
          <div class="sections-container">
            <!-- 1. Introduction -->
            <section id="introduction" class="policy-section">
              <h3 class="section-heading">1. Introduction</h3>
              <p>
                SaveAnimals is a specialized wildlife rescue, monitoring, and tracking system. The app is used by authorized field workers, veterinarians, rescue teams, and conservation analysts to report, document, and manage wildlife rescue cases, human-wildlife conflict incidents, and wildlife health logs. Because the app deals with critical conservation data and field operations, we maintain high standards of privacy and data security.
              </p>
              <p>
                By downloading, accessing, or using the SaveAnimals Mobile App or Web Panel, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with any terms of this policy, please do not use the application.
              </p>
            </section>

            <!-- 2. Information We Collect -->
            <section id="information-collection" class="policy-section">
              <h3 class="section-heading">2. Information We Collect</h3>
              <p>
                We collect different types of information to enable safe, accurate, and secure tracking of animal rescue records. This includes:
              </p>
              
              <div class="data-types-grid">
                <div class="data-type-card">
                  <div class="card-icon-container">
                    <span class="emoji-icon">👤</span>
                  </div>
                  <h5>Personal Information</h5>
                  <p>
                    Account registration details including your <strong>Name, Email Address, Phone Number</strong>, and password. This is required to identify authorized personnel and prevent unauthorized system access.
                  </p>
                </div>

                <div class="data-type-card">
                  <div class="card-icon-container gps-icon">
                    <span class="emoji-icon">📍</span>
                  </div>
                  <h5>Precise Location Data</h5>
                  <p>
                    <strong>GPS latitude and longitude coordinates</strong> of animal rescue operations. This is critical for mapping wildlife incidents, locating injured animals, tracking patrol routes, and conducting regional scientific analysis.
                  </p>
                </div>

                <div class="data-type-card">
                  <div class="card-icon-container media-icon">
                    <span class="emoji-icon">📸</span>
                  </div>
                  <h5>Media & Images</h5>
                  <p>
                    <strong>Photos and videos</strong> that you capture or upload inside the app showing animal rescues, species details, carcass conditions, or habitat environments.
                  </p>
                </div>

                <div class="data-type-card">
                  <div class="card-icon-container tech-icon">
                    <span class="emoji-icon">📱</span>
                  </div>
                  <h5>Device & Tech Info</h5>
                  <p>
                    Unique device identifier (Device ID), device type, operating system version, browser type, IP address, and crash log analytics (collected during login and use of the application).
                  </p>
                </div>
              </div>
            </section>

            <!-- 3. Precise Location Usage -->
            <section id="location-usage" class="policy-sectionHighlight">
              <div class="badge-accent">Core Feature</div>
              <h3 class="section-heading text-white">3. Background & Precise Location Usage</h3>
              <p class="text-light">
                <strong>SaveAnimals relies heavily on precise location services.</strong> 
                The primary purpose of this application is to assist conservationists and authorities in responding to wildlife distress. To do this effectively:
              </p>
              <ul class="text-white custom-list">
                <li>We fetch your precise GPS location when you log a new rescue entry or submit field survey records.</li>
                <li>Location data is uploaded to our secure servers to generate live incident maps on the admin panel, allowing rescue teams to coordinate actions.</li>
                <li>Location tracking may run in the background (if explicitly permitted by you) during active patrol/trek logs to map boundary pathways and migration patterns.</li>
              </ul>
              <p class="text-light-muted">
                You can grant or revoke location access at any time through your device settings. However, disabling location services will significantly limit the core rescue reporting capabilities of the application.
              </p>
            </section>

            <!-- 4. How We Use Your Data -->
            <section id="data-usage" class="policy-section">
              <h3 class="section-heading">4. How We Use Your Data</h3>
              <p>We use the collected information for purposes vital to wildlife conservation and system security:</p>
              <table class="policy-table">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Purpose of Use</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Name & Contact Info</strong></td>
                    <td>Authentication, user audit logs for rescue reporting, and emergency notifications.</td>
                  </tr>
                  <tr>
                    <td><strong>GPS Location Coordinates</strong></td>
                    <td>Mapping incident sites, verifying species locations, and routing emergency support teams.</td>
                  </tr>
                  <tr>
                    <td><strong>Photos & Rescue Media</strong></td>
                    <td>Verifying species classification, analyzing animal health conditions, and legal/conservation documentation.</td>
                  </tr>
                  <tr>
                    <td><strong>Device ID & Logs</strong></td>
                    <td>Securing endpoints, diagnosing system bugs, and improving app performance.</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <!-- 5. Data Sharing and Disclosure -->
            <section id="data-sharing" class="policy-section">
              <h3 class="section-heading">5. Data Sharing and Disclosure</h3>
              <p>
                We treat wildlife and conservation data with utmost confidentiality. Your data is never sold to third-party marketing companies. Data is shared exclusively under the following terms:
              </p>
              <div class="sharing-rules">
                <div class="rule-item">
                  <span class="rule-num">01</span>
                  <div>
                    <strong>Conservation Organizations:</strong> Authorized data is shared internally with the <strong>Wildlife Trust of India (WTI)</strong> and the <strong>International Fund for Animal Welfare (IFAW)</strong> to assist in veterinary analysis, regional ecological planning, and conservation reports.
                  </div>
                </div>
                <div class="rule-item">
                  <span class="rule-num">02</span>
                  <div>
                    <strong>Forest Departments & Government Authorities:</strong> We share location and case reports with forest department officials to comply with local wildlife protection laws and support lawful wildlife enforcement operations.
                  </div>
                </div>
                <div class="rule-item">
                  <span class="rule-num">03</span>
                  <div>
                    <strong>Legal Requirements:</strong> If required by court orders or to defend the safety, rights, or property of our users, developers, and wildlife, we may disclose information to law enforcement agencies.
                  </div>
                </div>
              </div>
            </section>

            <!-- 6. Security of Data -->
            <section id="data-security" class="policy-section">
              <h3 class="section-heading">6. Security of Data</h3>
              <p>
                We implement robust security measures to protect your conservation records and personal information from unauthorized access, alteration, disclosure, or destruction.
              </p>
              <p>
                Our databases are hosted in secured, state-of-the-art cloud facilities. APIs are restricted to authorized clients using secure JWT (JSON Web Tokens) or HTTPS encryption protocols. While we strive to use commercially acceptable means to protect your personal data, remember that no method of transmission over the internet or method of electronic storage is 100% secure.
              </p>
            </section>

            <!-- 7. Data Retention -->
            <section id="data-retention" class="policy-section">
              <h3 class="section-heading">7. Data Retention</h3>
              <p>
                We retain personal and operational data only for as long as necessary to:
              </p>
              <ul class="custom-list-dark">
                <li>Provide the services</li>
                <li>Maintain records for conservation and operational purposes</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements and policies</li>
              </ul>
              <p>
                When data is no longer required, we take reasonable steps to securely delete or anonymize it where appropriate.
              </p>
            </section>

            <!-- 8. Children's Privacy -->
            <section id="children-privacy" class="policy-section">
              <h3 class="section-heading">8. Children's Privacy</h3>
              <p>
                Our services are intended for professional conservationists, field rangers, veterinary doctors, and authorized adults. They are not intended for children under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us so that we can take immediate action to delete that information.
              </p>
              <p>
                <strong>SaveAnimals has zero tolerance for Child Sexual Abuse and Exploitation (CSAE) and Child Sexual Abuse Material (CSAM).</strong> Creating, uploading, storing, sharing, requesting, promoting, facilitating, or distributing CSAE or CSAM through the SaveAnimals mobile app or web platform is strictly prohibited.
              </p>
              <p>
                Our complete prohibited-conduct, reporting, review, and enforcement requirements are published in the
                <router-link to="/child-safety-standards"><strong>SaveAnimals Child Safety Standards</strong></router-link>.
                Child-safety concerns may be reported to the <strong>Child Safety Team — Leopard Tech Labs Pvt. Ltd.</strong> at
                <a href="mailto:info@leopardtechlabs.com?subject=SaveAnimals%20Child%20Safety%20Report">info@leopardtechlabs.com</a>.
                Do not attach or redistribute suspected CSAM in a report.
              </p>
            </section>

            <!-- 9. Your Choices -->
            <section id="your-choices" class="policy-section">
              <h3 class="section-heading">9. Your Choices</h3>
              <p>You may:</p>
              <ul class="custom-list-dark">
                <li>Disable location access through your device settings</li>
                <li>Stop using the application at any time</li>
                <li>Contact us to request deletion or correction of information where applicable</li>
                <li>Manage certain app permissions through your device or browser settings</li>
              </ul>
            </section>

            <!-- 10. Contact Us -->
            <section id="contact-us" class="policy-section">
              <h3 class="section-heading">10. Contact Us & Support</h3>
              <p>
                If you have any questions, suggestions, or concerns regarding this Privacy Policy or would like to follow up on a data deletion request, please contact us:
              </p>
              
              <div class="contact-cards">
                <div class="contact-card">
                  <h6>Leopard Tech Labs Pvt. Ltd.</h6>
                  <p class="text-muted">Primary Developers</p>
                  <hr/>
                  <p><strong>Email:</strong> <a href="mailto:info@leopardtechlabs.com">info@leopardtechlabs.com</a></p>
                  <p><strong>Website:</strong> <a href="https://leopardtechlabs.com" target="_blank" rel="noopener">leopardtechlabs.com</a></p>
                </div>

                <div class="contact-card">
                  <h6>Child Safety Team — Leopard Tech Labs Pvt. Ltd.</h6>
                  <p class="text-muted">Designated Child-Safety Contact</p>
                  <hr/>
                  <p><strong>Email:</strong> <a href="mailto:info@leopardtechlabs.com?subject=SaveAnimals%20Child%20Safety%20Report">info@leopardtechlabs.com</a></p>
                  <p><router-link to="/child-safety-standards">View Child Safety Standards</router-link></p>
                </div>

                <div class="contact-card">
                  <h6>Wildlife Trust of India (WTI)</h6>
                  <p class="text-muted">Conservation Partners</p>
                  <hr/>
                  <p><strong>Email:</strong> <a href="mailto:info@wti.org.in">info@wti.org.in</a></p>
                  <p><strong>Website:</strong> <a href="https://wti.org.in" target="_blank" rel="noopener">wti.org.in</a></p>
                </div>
              </div>
            </section>

            <!-- 11. Changes to This Policy -->
            <section id="policy-changes" class="policy-section">
              <h3 class="section-heading">11. Changes to This Policy</h3>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, operational needs, legal requirements, or platform policies. Any changes will be posted on this page with an updated revision date.
              </p>
            </section>
          </div>
        </article>
      </div>
    </main>

    <!-- Custom Standalone Footer -->
    <footer class="policy-footer">
      <div class="footer-container">
        <p>&copy; 2026 SaveAnimals | Developed by Leopard Tech Labs Pvt. Ltd. in collaboration with Wildlife Trust of India & IFAW.</p>
        <p class="footer-links">
          <a href="#" @click.prevent="scrollTo('introduction')">Back to Top</a> &bull;
          <router-link to="/child-safety-standards">Child Safety Standards</router-link> &bull;
          <a href="/login">Portal Login</a>
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      activeSection: 'introduction',
      deletionSubmitted: false,
      deletionEmail: '',
      deletionForm: {
        name: '',
        email: '',
        phone: '',
        reason: '',
        confirm: false
      },
      sections: [
        { id: 'introduction', title: '1. Introduction' },
        { id: 'information-collection', title: '2. Information We Collect' },
        { id: 'location-usage', title: '3. Precise Location' },
        { id: 'data-usage', title: '4. How We Use Data' },
        { id: 'data-sharing', title: '5. Data Sharing' },
        { id: 'data-security', title: '6. Data Security' },
        { id: 'data-retention', title: '7. Data Retention' },
        { id: 'children-privacy', title: '8. Children\'s Privacy' },
        { id: 'your-choices', title: '9. Your Choices' },
        { id: 'contact-us', title: '10. Contact Support' },
        { id: 'policy-changes', title: '11. Policy Changes' }
      ]
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    scrollTo(id) {
      this.activeSection = id;
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    handleScroll() {
      const scrollPosition = window.scrollY + 100;
      for (const section of this.sections) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            this.activeSection = section.id;
            break;
          }
        }
      }
    },
    submitDeletion() {
      // Form submission simulation
      this.deletionEmail = this.deletionForm.email;
      this.deletionSubmitted = true;
      
      // Clear form
      this.deletionForm = {
        name: '',
        email: '',
        phone: '',
        reason: '',
        confirm: false
      };
    }
  }
};
</script>

<style scoped>
/* Premium Nature and Wildlife Conservation Aesthetic Design */

.privacy-policy-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #2c3e50;
  background-color: #f7fbf9;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  line-height: 1.6;
}

/* Header Styling */
.brand-header {
  background: linear-gradient(135deg, #1b4332 0%, #081c15 100%);
  padding: 1.5rem 2rem;
  border-bottom: 4px solid #52b788;
  color: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.brand-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.brand-logo {
  height: 60px;
  object-fit: contain;
  background: white;
  padding: 4px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.brand-title {
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.5px;
  background: linear-gradient(to right, #ffffff, #d8f3dc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-tagline {
  font-size: 0.9rem;
  color: #95d5b2;
  margin: 0;
  font-weight: 500;
}

.partner-logos {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.partner-logo {
  height: 40px;
  background: white;
  border-radius: 6px;
  padding: 3px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  object-fit: contain;
  transition: transform 0.3s ease;
}

.partner-logo:hover {
  transform: translateY(-2px);
}

/* Main Layout */
.policy-main-content {
  flex: 1;
  padding: 2.5rem 1rem;
}

.policy-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2.5rem;
}

/* Sidebar Navigation */
.policy-sidebar {
  display: block;
}

.sidebar-sticky {
  position: sticky;
  top: 2rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(27, 67, 50, 0.04);
  border: 1px solid #e1e9e5;
}

.sidebar-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1b4332;
  margin-bottom: 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #d8f3dc;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-list li {
  margin-bottom: 0.5rem;
}

.nav-list a {
  display: block;
  padding: 0.5rem 0.75rem;
  color: #5c6b64;
  text-decoration: none;
  font-size: 0.9rem;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.nav-list li.active a, .nav-list a:hover {
  background-color: #e8f5e9;
  color: #1b4332;
  font-weight: 600;
  padding-left: 1rem;
}

.delete-badge {
  margin-top: 1.5rem;
  padding: 0.75rem;
  background-color: #ffebee;
  color: #c62828;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  border: 1px solid #ffcdd2;
  transition: all 0.2s ease;
}

.delete-badge:hover {
  background-color: #ffcdd2;
  box-shadow: 0 4px 10px rgba(198, 40, 40, 0.15);
}

/* Document Body Card */
.policy-article {
  background: #ffffff;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(27, 67, 50, 0.05);
  border: 1px solid #e2ece7;
}

.intro-card {
  padding-bottom: 2rem;
  border-bottom: 1px solid #e8f0ec;
  margin-bottom: 2.5rem;
}

.doc-title {
  font-size: 2.4rem;
  font-weight: 800;
  color: #1b4332;
  margin-bottom: 0.5rem;
}

.meta-row {
  display: flex;
  gap: 1.5rem;
  font-size: 0.9rem;
  color: #718096;
  margin-bottom: 1.5rem;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.green-label {
  color: #2d6a4f;
}

.intro-text {
  font-size: 1.1rem;
  color: #4a5568;
}

/* Sections details */
.policy-section {
  margin-bottom: 3rem;
  scroll-margin-top: 2rem;
}

.section-heading {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1b4332;
  margin-bottom: 1.2rem;
  position: relative;
  padding-bottom: 0.4rem;
}

.section-heading::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 40px;
  height: 3px;
  background-color: #52b788;
  border-radius: 2px;
}

/* Data Grid cards */
.data-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.data-type-card {
  background-color: #f7fbf9;
  border: 1px solid #e1e9e5;
  border-radius: 10px;
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.data-type-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(45, 106, 79, 0.08);
  border-color: #b7e4c7;
}

.card-icon-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.emoji-icon {
  font-size: 1.5rem;
}

.gps-icon {
  background-color: #e0f7fa;
}

.media-icon {
  background-color: #ede7f6;
}

.tech-icon {
  background-color: #fff3e0;
}

.data-type-card h5 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1b4332;
  margin-bottom: 0.5rem;
}

.data-type-card p {
  font-size: 0.88rem;
  color: #5c6b64;
  margin: 0;
  line-height: 1.5;
}

/* Highlight section for location data */
.policy-sectionHighlight {
  background: linear-gradient(135deg, #2d6a4f 0%, #1b4332 100%);
  border-radius: 12px;
  padding: 2.25rem;
  color: #ffffff;
  margin-bottom: 3rem;
  scroll-margin-top: 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(27, 67, 50, 0.1);
}

.badge-accent {
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background-color: #52b788;
  color: #081c15;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
}

.text-white {
  color: #ffffff !important;
}

.text-light {
  color: #d8f3dc !important;
}

.text-light-muted {
  color: #b7e4c7 !important;
  font-size: 0.9rem;
  margin-top: 1rem;
}

.custom-list {
  padding-left: 1.25rem;
  margin-bottom: 1rem;
}

.custom-list li {
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.custom-list-dark {
  padding-left: 1.25rem;
  margin: 1rem 0;
  color: #4a5568;
}

.custom-list-dark li {
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Table styling */
.policy-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.95rem;
}

.policy-table th, .policy-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e1e9e5;
}

.policy-table th {
  background-color: #f2f7f4;
  color: #1b4332;
  font-weight: 700;
}

.policy-table tr:hover {
  background-color: #f9fbf9;
}

/* Sharing Rules */
.sharing-rules {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 1.5rem;
}

.rule-item {
  display: flex;
  gap: 1.25rem;
  background-color: #fdfefe;
  border: 1px solid #e8f0ec;
  padding: 1.25rem;
  border-radius: 8px;
}

.rule-num {
  font-size: 1.8rem;
  font-weight: 800;
  color: #52b788;
  line-height: 1;
}

/* Deletion Section */
.policy-sectionDeletion {
  margin-bottom: 3rem;
  scroll-margin-top: 2rem;
  background-color: #fff8f8;
  border: 1px solid #ffe3e3;
  border-radius: 12px;
  padding: 2rem;
}

.deletion-note {
  background-color: #fff0f0;
  border-left: 4px solid #e53935;
  padding: 1rem;
  border-radius: 0 8px 8px 0;
  font-size: 0.9rem;
  color: #c62828;
  margin-bottom: 2rem;
}

.deletion-form-container {
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(198, 40, 40, 0.05);
  border: 1px solid #ffebee;
}

.form-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #c62828;
  margin-bottom: 0.25rem;
}

.form-sub {
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
}

.form-group-custom {
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-group-custom label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #4a5568;
}

.form-group-custom input, .form-group-custom textarea {
  padding: 0.75rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 0.95rem;
  background-color: #fcfcfc;
  transition: all 0.2s ease;
}

.form-group-custom input:focus, .form-group-custom textarea:focus {
  outline: none;
  border-color: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.15);
  background-color: #ffffff;
}

.form-row-custom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.checkbox-container {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  align-items: flex-start;
}

.checkbox-container input {
  margin-top: 0.3rem;
  accent-color: #c62828;
}

.checkbox-container label {
  font-size: 0.85rem;
  color: #4a5568;
  line-height: 1.4;
  cursor: pointer;
}

.btn-delete {
  width: 100%;
  padding: 0.9rem;
  background-color: #e53935;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.btn-delete:hover {
  background-color: #b71c1c;
  box-shadow: 0 4px 12px rgba(183, 28, 28, 0.2);
}

.btn-delete:disabled {
  background-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
  box-shadow: none;
}

/* Success alert custom */
.alert-success-custom {
  background-color: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  padding: 1.5rem;
  color: #2e7d32;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2e7d32;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
}

.alert-success-custom h5 {
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.alert-success-custom p {
  margin: 0;
  font-size: 0.9rem;
  color: #388e3c;
}

/* Contact support cards */
.contact-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.contact-card {
  background: white;
  border: 1px solid #e1e9e5;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.contact-card h6 {
  font-size: 1rem;
  font-weight: 700;
  color: #1b4332;
  margin-bottom: 0.25rem;
}

.contact-card p {
  font-size: 0.88rem;
  margin-bottom: 0.4rem;
}

.contact-card hr {
  margin: 0.75rem 0;
  border: 0;
  border-top: 1px solid #f0f4f2;
}

.contact-card a {
  color: #2d6a4f;
  text-decoration: none;
  font-weight: 600;
}

.contact-card a:hover {
  text-decoration: underline;
  color: #52b788;
}

/* Footer Section */
.policy-footer {
  background-color: #081c15;
  color: #95d5b2;
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.85rem;
  border-top: 3px solid #52b788;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-links {
  margin-top: 0.75rem;
}

.footer-links a {
  color: #ffffff;
  text-decoration: none;
  margin: 0 0.5rem;
  font-weight: 600;
}

.footer-links a:hover {
  color: #52b788;
}

/* Responsive adjustments */
@media (max-width: 900px) {
  .policy-container {
    grid-template-columns: 1fr;
  }
  .policy-sidebar {
    display: none;
  }
}

@media (max-width: 600px) {
  .brand-header {
    padding: 1rem;
  }
  .header-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .partner-logos {
    width: 100%;
    justify-content: flex-start;
  }
  .partner-logo {
    height: 32px;
  }
  .policy-article {
    padding: 1.5rem;
  }
  .doc-title {
    font-size: 1.8rem;
  }
  .form-row-custom {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .data-types-grid {
    grid-template-columns: 1fr;
  }
  .contact-cards {
    grid-template-columns: 1fr;
  }
}
</style>
